import { Box, Flex } from '@chakra-ui/react';
import InfiniteScroll from 'react-infinite-scroller';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';
import ListingGridFilters from '../ListingGridFilters/ListingGridFilters';
import ListingAccordion from '../ListingAccordion/ListingAccordion';
import ListingGridSkeleton from '../ListingGridSkeleton/ListingGridSkeleton';
import EmptyState from '../../EmptyState/EmptyState';
import { useListingFilterSort } from 'hooks/useListingFilterSort';
import { PaginationModel, ListingModel } from 'models';

export interface PaginatedListingGridProps {
  paginatedListings: PaginationModel<ListingModel>;
  onScrollEndLoad?: () => void;
  onPageSelect?: (page: number) => void;
  isLoading: boolean;
  showFilters?: boolean;
  showFiltersLabel?: boolean;
  scrollMode?: boolean;
  showAssociatedItems?: boolean;
  showAssociationAction?: boolean;
  onListingItemClick?: (id: string, name: string) => void;
  onListingArtistClick?: (id: string, name: string) => void;
  onItemSuggestionClick?: (listing: ListingModel) => void;
  onTagListingClick?: (listing: ListingModel) => void;
}

export default function PaginatedListingGrid({
  paginatedListings,
  isLoading,
  onScrollEndLoad,
  onPageSelect,
  scrollMode = true,
  showFilters = true,
  showFiltersLabel = true,
  showAssociatedItems = true,
  showAssociationAction = true,
  onListingItemClick,
  onListingArtistClick,
  onItemSuggestionClick,
  onTagListingClick,
}: PaginatedListingGridProps) {
  const {
    rawQuery,
    onSearch,
    selectedYears,
    setSelectedYears,
    selectedPrices,
    setSelectedPrices,
    showOriginal,
    setShowOriginal,
    showSet,
    setShowSet,
    sortKey,
    setSortKey,
    sortType,
    setSortType,
    displayFilters,
    setDisplayFilters,
    filteredListings,
    hasFiltersApplied,
    isOnFilterMode,
    resetFilters,
  } = useListingFilterSort(paginatedListings.data);

  const data = isOnFilterMode() ? filteredListings : paginatedListings.data;
  const hasMore = paginatedListings.hasMorePages() && !isOnFilterMode();
  const isFetchingMore = isLoading && paginatedListings.data.length > 0;


  return (
    <Box width="100%">
      <ListingGridFilters
        showFilters={showFilters}
        showFiltersLabel={showFiltersLabel}
        rawQuery={rawQuery}
        onSearch={onSearch}
        displayFilters={displayFilters}
        setDisplayFilters={setDisplayFilters}
        sortKey={sortKey}
        onSortBySelect={setSortKey}
        sortType={sortType}
        onSortTypeToggle={() => setSortType(t => (t === 'asc' ? 'desc' : 'asc'))}
        listings={paginatedListings.data}
        showTypeFilter={true}
        defaultSaleDateRange={selectedYears}
        defaultPriceRange={selectedPrices}
        defaultShowOriginal={showOriginal}
        defaultShowSet={showSet}
        showClearOption={hasFiltersApplied()}
        onListingSaleDateRangeSelect={(min, max) => setSelectedYears([min, max])}
        onListingPriceRangeSelect={(min, max) => setSelectedPrices([min, max])}
        onListingShowOriginalToggle={() => setShowOriginal(o => !o)}
        onListingShowSetToggle={() => setShowSet(s => !s)}
        onResetFilters={resetFilters}
      />

      {scrollMode && onScrollEndLoad ? (
        isLoading && paginatedListings.data.length === 0 ? (
          <ListingGridSkeleton rows={10} />
        ) : data.length === 0 ? (
          <EmptyState
            header="No Listings"
            description="No Results Found"
            showButton={isOnFilterMode()}
            onButtonClick={resetFilters}
            buttonText="Clear Filters"
          />
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={onScrollEndLoad}
            hasMore={hasMore}
            loader={
              isFetchingMore ? (
                <Box p="16px">
                  <ListingGridSkeleton rows={20} />
                </Box>
              ) : undefined
            }
            useWindow
            initialLoad={false}
          >
            <Flex direction="column" gap="12px">
              {data.map((listing, index) => (
                <ListingAccordion
                  key={`${listing.id}-${index}`}
                  listing={listing}
                  onItemClick={onListingItemClick}
                  onArtistClick={onListingArtistClick}
                  onItemSuggestionClick={onItemSuggestionClick}
                  onTagListingClick={onTagListingClick}
                  showAssociatedItem={showAssociatedItems}
                  showAssociationAction={showAssociationAction}
                />
              ))}
            </Flex>
          </InfiniteScroll>
        )
      ) : isLoading ? (
        <ListingGridSkeleton rows={10} />
      ) : data.length > 0 ? (
        <Flex direction="column" gap="12px">
          {data.map(listing => (
            <ListingAccordion
              key={listing.id}
              listing={listing}
              onItemClick={onListingItemClick}
              onArtistClick={onListingArtistClick}
              onItemSuggestionClick={onItemSuggestionClick}
              onTagListingClick={onTagListingClick}
              showAssociatedItem={showAssociatedItems}
              showAssociationAction={showAssociationAction}
            />
          ))}
        </Flex>
      ) : (
        <EmptyState
          header="No Listings"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={resetFilters}
          buttonText="Clear Filters"
        />
      )}

      {!scrollMode && (
        <PaginationFooter
          currentPage={paginatedListings.currentPage}
          lastPage={paginatedListings.totalPages}
          siblingsCount={2}
          onPageChange={onPageSelect!}
        />
      )}
    </Box>
  );
}